import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useClientAccess } from '../../hooks/useClientAccess';
import { useI18n } from '../../contexts/I18nContext';
import { apiClient } from '../../services/apiClient';
import { clientService } from '../../services/clientService';
import { TypeFilter } from '../common/TypeSelector';
import { Pagination } from '../common/Pagination';
import { ConfigTemplateDetailModal } from './ConfigTemplateDetailModal';
import { ConfigTemplateListItem, getPriorityLevel } from '../../types/configTemplate';
import { Client } from '../../types/client';
import { safeStorage } from '../../utils/safeStorage';
import { isAbortError } from '../../utils/abortError';

interface ConfigTemplatesTableProps {
  onTemplateSelect?: (template: ConfigTemplateListItem) => void;
  refreshTrigger?: number;
  canEdit?: boolean;
  isSuperAdmin?: boolean;
}

export const ConfigTemplatesTable: React.FC<ConfigTemplatesTableProps> = ({
  onTemplateSelect,
  refreshTrigger,
  canEdit = false,
  isSuperAdmin = false
}) => {
  const { isAuthenticated } = useAuth();
  const { isAdmin, isSuperAdmin: isSuperAdminFromHook } = useClientAccess();
  const canSeeInactive = isAdmin || isSuperAdmin || isSuperAdminFromHook;
  const { formatDate, t } = useI18n();
  // Soft-delete: admins can opt in to see deactivated templates. Default off.
  const [showInactive, setShowInactive] = useState(false);
  const [templates, setTemplates] = useState<ConfigTemplateListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  // Debounced copy of `searchTerm` — pushed to the server-side filter.
  // Page resets to 1 whenever the debounced value changes.
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sortField, setSortField] = useState<keyof ConfigTemplateListItem>('priority');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [clientList, setClientList] = useState<Client[]>([]);
  const [localRefresh, setLocalRefresh] = useState(0);
  // Server pagination state — populated from the list response.
  const [serverTotalItems, setServerTotalItems] = useState(0);
  const [serverTotalPages, setServerTotalPages] = useState(0);
  // Stats from /v2/config-templates/stats — fleet-wide aggregates
  // (filtered by the caller's client-group RBAC). The list endpoint
  // only returns the current page so we'd never see fleet totals.
  const [statsTotal, setStatsTotal] = useState<number | null>(null);
  const [statsActive, setStatsActive] = useState<number | null>(null);
  const [statsHighPriority, setStatsHighPriority] = useState<number | null>(null);

  // Sequential-merge info banner — dismissed state persists in localStorage
  const BANNER_STORAGE_KEY = 'configTemplates.mergeBannerDismissed';
  const [mergeBannerDismissed, setMergeBannerDismissed] = useState<boolean>(() => {
    try {
      return safeStorage.getItem(BANNER_STORAGE_KEY) === '1';
    } catch {
      return false;
    }
  });
  const dismissMergeBanner = () => {
    try { safeStorage.setItem(BANNER_STORAGE_KEY, '1'); } catch { /* noop */ }
    setMergeBannerDismissed(true);
  };

  // Filter states
  const [clientFilter, setClientFilter] = useState('');
  const [deviceTypeFilter, setDeviceTypeFilter] = useState<string>('');
  const [priorityFilter, setPriorityFilter] = useState<string>('');

  // Get axios instance from apiClient
  const axiosInstance = useMemo(() => apiClient.getAxiosInstance(), []);

  // Debounce search input: typing should not slam the API with a
  // request per keystroke. 250 ms matches the DevicesTable cadence.
  useEffect(() => {
    const handle = setTimeout(() => setDebouncedSearch(searchTerm), 250);
    return () => clearTimeout(handle);
  }, [searchTerm]);

  // Any filter/search change should bring us back to page 1 so the
  // user doesn't land on an empty page of a now-shorter result set.
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, deviceTypeFilter, showInactive]);

  // Fetch templates from API — server-side paginated. The backend
  // applies search, device_type, include_inactive, page, limit and
  // returns { items, total, page, limit, pages }. Client filters
  // (clientFilter by name, priorityFilter bucket) and the client-side
  // sort still operate over the returned page.
  useEffect(() => {
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }

    const ac = new AbortController();
    const fetchTemplates = async () => {
      try {
        setLoading(true);
        setError(null);

        const listParams: Record<string, any> = {
          page: currentPage,
          limit: itemsPerPage,
        };
        if (debouncedSearch.trim()) listParams.search = debouncedSearch.trim();
        if (deviceTypeFilter) listParams.device_type = deviceTypeFilter;
        if (showInactive && canSeeInactive) listParams.include_inactive = true;

        const response = await axiosInstance.get('/v2/config-templates', {
          params: listParams,
          signal: ac.signal,
        });

        const templatesData: ConfigTemplateListItem[] = response.data.items.map((tmpl: any) => ({
          id: String(tmpl.id),
          name: tmpl.name,
          description: tmpl.description,
          client_id: tmpl.client_id || '00000000-0000-0000-0000-000000000000',
          client_ids: tmpl.client_ids || [tmpl.client_id || '00000000-0000-0000-0000-000000000000'],
          device_type: tmpl.device_type,
          priority: tmpl.priority ?? 50,
          is_active: tmpl.is_active,
          created_at: tmpl.created_at,
          updated_at: tmpl.updated_at
        }));

        setTemplates(templatesData);
        setServerTotalItems(response.data.total ?? templatesData.length);
        setServerTotalPages(response.data.pages ?? 1);
      } catch (err: any) {
        if (isAbortError(err)) return;
        console.error('[CONFIG TEMPLATES TABLE] Error fetching templates:', err);

        if (err.response?.status === 404 || err.response?.status === 500) {
          setTemplates([]);
          setServerTotalItems(0);
          setServerTotalPages(0);
          setError('Configuration templates table not initialized. Please run database migration.');
        } else {
          setError(err.message || 'Failed to fetch configuration templates');
        }
      } finally {
        if (!ac.signal.aborted) setLoading(false);
      }
    };

    fetchTemplates();
    return () => ac.abort();
  }, [
    isAuthenticated, refreshTrigger, localRefresh, axiosInstance,
    showInactive, canSeeInactive, currentPage, itemsPerPage,
    debouncedSearch, deviceTypeFilter,
  ]);

  // Fetch clients for name lookup. clientService is process-cached and
  // shared, so we use a `cancelled` flag rather than aborting.
  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;
    (async () => {
      try {
        const clients = await clientService.fetchAllClients();
        if (cancelled) return;
        setClientList(clients);
      } catch (err) {
        if (cancelled) return;
        console.error('[CONFIG TEMPLATES TABLE] Error fetching clients:', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated]);

  // Helper function to get client name by ID. Returns null for
  // unresolved ids (deleted client, RBAC-hidden, or cache miss) so
  // the caller can filter them out instead of rendering "Unknown".
  const getClientName = (clientId: string): string | null => {
    if (clientId === '00000000-0000-0000-0000-000000000000') {
      return 'Global';
    }
    const client = clientList.find(c => c.id === clientId);
    return client?.name || null;
  };

  // Helper function to display multiple clients. Only resolved names
  // are surfaced — unresolved client_ids are filtered out entirely.
  // Falls back to an em-dash when nothing resolves so the cell isn't
  // visually empty.
  const getClientsDisplay = (template: ConfigTemplateListItem): React.ReactNode => {
    const ids = template.client_ids || [template.client_id];
    const names = ids.map(getClientName).filter((n): n is string => n !== null);
    if (names.length === 0) return '—';
    if (names.length === 1) return names[0];

    const moreCount = names.length - 1;
    return (
      <span className="multi-client-display" title={names.join(', ')}>
        {names[0]} <span className="more-badge">+{moreCount}</span>
      </span>
    );
  };

  // Fleet-wide stats from /v2/config-templates/stats. The list
  // endpoint only returns the current page; computing from `templates`
  // would silently undercount once pagination kicks in.
  useEffect(() => {
    if (!isAuthenticated) return;
    const ac = new AbortController();
    (async () => {
      try {
        const res = await axiosInstance.get('/v2/config-templates/stats', { signal: ac.signal });
        const total: number = res.data?.total_templates ?? 0;
        const active: number = res.data?.active_templates ?? 0;
        const highPriority: number = res.data?.templates_by_priority?.['high (76-100)'] ?? 0;
        setStatsTotal(total);
        setStatsActive(active);
        setStatsHighPriority(highPriority);
      } catch (e: any) {
        if (isAbortError(e)) return;
        // Stats are best-effort — the table still works without them.
        // eslint-disable-next-line no-console
        console.warn('[CONFIG TEMPLATES TABLE] stats fetch failed:', e?.message || e);
      }
    })();
    return () => ac.abort();
  }, [isAuthenticated, refreshTrigger, localRefresh, axiosInstance]);

  const stats = useMemo(() => {
    const totalTemplates = statsTotal ?? 0;
    const activeTemplates = statsActive ?? 0;
    // /stats reports total (all) + active. Inactive is the delta.
    const inactiveTemplates = statsTotal != null && statsActive != null
      ? Math.max(0, statsTotal - statsActive)
      : 0;
    const highPriorityTemplates = statsHighPriority ?? 0;
    return { totalTemplates, activeTemplates, inactiveTemplates, highPriorityTemplates };
  }, [statsTotal, statsActive, statsHighPriority]);

  // Filter and sort templates — `templates` is already the current
  // page from the server (search + device_type + include_inactive are
  // server-applied). clientFilter (text-match against client *name*,
  // backend only takes UUID) and priorityFilter (bucket) remain
  // client-only over the visible page.
  const filteredAndSortedTemplates = useMemo(() => {
    let filtered = templates.filter((template) => {
      // Client filter (search by client name) — client-only.
      const clientName = (getClientName(template.client_id) || '').toLowerCase();
      const matchesClient = !clientFilter || clientName.includes(clientFilter.toLowerCase());

      // Priority filter — bucket, client-only.
      let matchesPriority = true;
      if (priorityFilter) {
        switch (priorityFilter) {
          case 'high':
            matchesPriority = template.priority >= 76;
            break;
          case 'medium':
            matchesPriority = template.priority >= 51 && template.priority <= 75;
            break;
          case 'normal':
            matchesPriority = template.priority >= 26 && template.priority <= 50;
            break;
          case 'low':
            matchesPriority = template.priority >= 1 && template.priority <= 25;
            break;
        }
      }

      return matchesClient && matchesPriority;
    });

    // Sort over the current page. The backend sorts by priority DESC
    // by default; click a header to override locally for the visible
    // rows. (Cross-page sort would require sort params in the API.)
    filtered.sort((a, b) => {
      const aValue = a[sortField];
      const bValue = b[sortField];

      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return sortDirection === 'asc' ? 1 : -1;
      if (bValue == null) return sortDirection === 'asc' ? -1 : 1;

      let comparison = 0;
      if (aValue < bValue) comparison = -1;
      if (aValue > bValue) comparison = 1;

      return sortDirection === 'desc' ? -comparison : comparison;
    });

    return filtered;
    // getClientName is a stable lookup over clientList — including it would
    // force the memo to recompute on every render since the function
    // identity changes each time.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templates, clientFilter, priorityFilter, sortField, sortDirection, clientList]);

  const handleSort = (field: keyof ConfigTemplateListItem) => {
    if (field === sortField) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  const handleRowClick = (template: ConfigTemplateListItem) => {
    setSelectedTemplateId(template.id);
    setIsModalOpen(true);
    if (onTemplateSelect) {
      onTemplateSelect(template);
    }
  };

  const getSortIcon = (field: keyof ConfigTemplateListItem) => {
    if (field !== sortField) return '';
    return sortDirection === 'asc' ? ' ^' : ' v';
  };

  const getTypeBadgeClass = (type: string) => {
    switch (type) {
      case 'voice': return 'type-badge voice';
      case 'local_alarm': return 'type-badge alarm';
      case 'dtmf_server': return 'type-badge dtmf';
      default: return 'type-badge';
    }
  };

  const getPriorityBadgeStyle = (priority: number) => {
    const level = getPriorityLevel(priority);
    return {
      backgroundColor: level.color,
      color: '#fff',
      padding: '2px 8px',
      borderRadius: '4px',
      fontSize: '12px',
      fontWeight: 500
    };
  };

  // Only block the whole page on the FIRST load. Subsequent server-side
  // refetches (triggered by every filter change once #85 added paging)
  // should not unmount the filter row, otherwise typing in the search
  // box blanks the filters mid-keystroke and the row reference goes
  // stale. Pattern mirrors DevicesTable.
  if (loading && templates.length === 0) {
    return (
      <div className="templates-loading">
        <div className="loading-spinner"></div>
        <p>Loading configuration templates...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="templates-error">
        <span className="error-icon">!</span>
        <h3>{t('configTemplates.errorLoading')}</h3>
        <p>{error}</p>
        <button className="retry-button" onClick={() => window.location.reload()}>{t('errors.retryAction')}</button>
      </div>
    );
  }

  return (
    <div className="templates-container">
      {!mergeBannerDismissed && (
        <div
          role="note"
          aria-label={t('templatePriority.mergeBannerTitle')}
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 12,
            padding: 12,
            marginBottom: 16,
            background: '#e3f2fd',
            border: '1px solid #90caf9',
            borderRadius: 6,
            color: '#0d47a1',
          }}
          data-testid="template-merge-banner"
        >
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>
              {t('templatePriority.mergeBannerTitle')}
            </div>
            <div style={{ fontSize: 13, lineHeight: 1.5 }}>
              {t('templatePriority.mergeBannerBody')}
            </div>
          </div>
          <button
            type="button"
            onClick={dismissMergeBanner}
            style={{
              background: 'transparent',
              border: '1px solid #0d47a1',
              color: '#0d47a1',
              borderRadius: 4,
              padding: '4px 10px',
              cursor: 'pointer',
              fontSize: 12,
              fontWeight: 600,
            }}
            data-testid="template-merge-banner-dismiss"
          >
            {t('templatePriority.mergeBannerDismiss')}
          </button>
        </div>
      )}

      {/* Stats Grid */}
      <div className="stats-grid">
        <div className="stat-card primary">
          <div className="stat-icon">T</div>
          <div className="stat-content">
            <div className="stat-value">{stats.totalTemplates}</div>
            <div className="stat-label">{t('configTemplates.totalTemplates')}</div>
          </div>
        </div>

        <div className="stat-card success">
          <div className="stat-icon">+</div>
          <div className="stat-content">
            <div className="stat-value">{stats.activeTemplates}</div>
            <div className="stat-label">{t('common.active')}</div>
          </div>
        </div>

        <div className="stat-card warning">
          <div className="stat-icon">-</div>
          <div className="stat-content">
            <div className="stat-value">{stats.inactiveTemplates}</div>
            <div className="stat-label">{t('common.inactive')}</div>
          </div>
        </div>

        <div className="stat-card secondary">
          <div className="stat-icon">!</div>
          <div className="stat-content">
            <div className="stat-value">{stats.highPriorityTemplates}</div>
            <div className="stat-label">{t('configTemplates.highPriority')}</div>
          </div>
        </div>
      </div>

      {/* Main Section */}
      <div className="templates-section">
        <div className="section-header">
          <div className="section-title">
            <h2>{t('configTemplates.allTemplates')}</h2>
          </div>
          <div className="section-controls">
            <div className="search-container">
              <input
                type="text"
                placeholder={t('configTemplates.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input search-input--ota-style"
                data-testid="field-templates-search"
                aria-label={t('configTemplates.searchPlaceholder')}
              />
            </div>
            <input
              type="text"
              id="clientFilter"
              placeholder={t('configTemplates.filterByClient')}
              value={clientFilter}
              onChange={(e) => {
                setClientFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="filter-input"
              aria-label={t('clients.client')}
            />
            <TypeFilter
              value={deviceTypeFilter}
              onChange={(value: string) => {
                setDeviceTypeFilter(value);
                setCurrentPage(1);
              }}
              allLabel={t('deviceTypes.allHardwareTypes')}
              className="filter-select"
              includeInactive={showInactive && canSeeInactive}
              ariaLabel={t('deviceTypes.hardwareTypes').slice(0, -1)}
            />
            <select
              id="priorityFilter"
              value={priorityFilter}
              onChange={(e) => {
                setPriorityFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="filter-select"
              aria-label={t('templatePriority.label')}
            >
              <option value="">{t('configTemplates.allPriorities')}</option>
              <option value="high">{t('configTemplates.highPriority')} (76-100)</option>
              <option value="medium">{t('common.medium') || 'Medium'} (51-75)</option>
              <option value="normal">{t('common.normal') || 'Normal'} (26-50)</option>
              <option value="low">{t('common.low') || 'Low'} (1-25)</option>
            </select>
            {canSeeInactive && (
              <label className="filter-toggle-inactive" title={t('common.showInactiveHint')}>
                <input
                  type="checkbox"
                  checked={showInactive}
                  onChange={(e) => {
                    setShowInactive(e.target.checked);
                    setCurrentPage(1);
                  }}
                />
                {t('common.showInactive')}
              </label>
            )}
            {(clientFilter || deviceTypeFilter || priorityFilter) && (
              <button
                className="clear-filters-btn"
                onClick={() => {
                  setClientFilter('');
                  setDeviceTypeFilter('');
                  setPriorityFilter('');
                  setCurrentPage(1);
                }}
              >
                Clear Filters
              </button>
            )}
          </div>
        </div>

        <div className="table-wrapper">
          <table className="templates-table">
            <thead>
              <tr>
                <th onClick={() => handleSort('client_id')} className="sortable">
                  Client{getSortIcon('client_id')}
                </th>
                <th onClick={() => handleSort('device_type')} className="sortable">
                  Hardware Type{getSortIcon('device_type')}
                </th>
                <th onClick={() => handleSort('priority')} className="sortable">
                  Priority{getSortIcon('priority')}
                </th>
                <th onClick={() => handleSort('name')} className="sortable">
                  Template Name{getSortIcon('name')}
                </th>
                <th onClick={() => handleSort('updated_at')} className="sortable">
                  Last Updated{getSortIcon('updated_at')}
                </th>
                {showInactive && <th>Status</th>}
              </tr>
            </thead>
            <tbody>
              {filteredAndSortedTemplates.map((template) => (
                <tr
                  key={template.id}
                  onClick={() => handleRowClick(template)}
                  className="template-row"
                >
                  <td className="template-client">
                    {getClientsDisplay(template)}
                  </td>
                  <td className="template-type">
                    <span className={getTypeBadgeClass(template.device_type)}>
                      {template.device_type.replace('_', ' ')}
                    </span>
                  </td>
                  <td className="template-priority">
                    <span style={getPriorityBadgeStyle(template.priority)}>
                      {template.priority} - {getPriorityLevel(template.priority).label}
                    </span>
                  </td>
                  <td className="template-name">
                    <div className="template-name-cell">
                      <strong>{template.name}</strong>
                      {template.description && (
                        <span className="template-description">{template.description}</span>
                      )}
                    </div>
                  </td>
                  <td className="last-updated">
                    {formatDate(template.updated_at)}
                  </td>
                  {showInactive && (
                    <td className="status">
                      <span className={`status-badge ${template.is_active ? 'active' : 'inactive'}`}>
                        {template.is_active ? 'ACTIVE' : 'INACTIVE'}
                      </span>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>

          {filteredAndSortedTemplates.length === 0 && !loading && (
            <div className="empty-state">
              <div className="empty-icon">T</div>
              <h3>{t('configTemplates.noTemplatesFound')}</h3>
            </div>
          )}
        </div>

        <Pagination
          page={currentPage}
          totalPages={serverTotalPages}
          totalItems={serverTotalItems}
          pageSize={itemsPerPage}
          itemLabel="templates"
          onPageChange={handlePageChange}
          onPageSizeChange={handleItemsPerPageChange}
        />
      </div>

      {/* Activity Footer */}
      <div className="templates-footer">
        <div className="activity-summary">
          <div className="activity-item">
            <span className="activity-icon">T</span>
            <span className="activity-text">
              <strong>{stats.totalTemplates}</strong> total templates
            </span>
          </div>
          <div className="activity-item">
            <span className="activity-icon">+</span>
            <span className="activity-text">
              <strong>{stats.activeTemplates}</strong> active
            </span>
          </div>
          <div className="activity-item">
            <span className="activity-icon">!</span>
            <span className="activity-text">
              <strong>{stats.highPriorityTemplates}</strong> high priority
            </span>
          </div>
        </div>
      </div>

      <ConfigTemplateDetailModal
        templateId={selectedTemplateId}
        isOpen={isModalOpen}
        canEdit={canEdit}
        isSuperAdmin={isSuperAdmin}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedTemplateId(null);
        }}
        onTemplateUpdated={() => {
          setIsModalOpen(false);
          setSelectedTemplateId(null);
          // Refresh the templates list
          setLocalRefresh(prev => prev + 1);
        }}
      />
    </div>
  );
};
